import { useNavigate, useSearchParams } from 'react-router-dom';
import { CUSTOMER_LOCALES } from '@capsico/contracts';
import { LOCALE_NAMES, useI18n } from '../i18n';
import { useProfileForm } from '../hooks/useProfile';
import {
  Button,
  Card,
  DraftBar,
  Field,
  ListSkeleton,
  Notice,
  PageHeader,
  Pill,
} from '../components/ui';

/** Profile (C9): first-time step after sign-in (name + diet required) and the later edit page. */
export function ProfilePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const first = params.get('first') === '1';
  const next = params.get('next') ?? '/account';
  const p = useProfileForm({
    messages: {
      saved: t('profile.saved'),
      nameRequired: t('profile.nameRequired'),
      dietRequired: t('profile.dietRequired'),
    },
    onSaved: () => navigate(first ? next : '/account', { replace: true }),
  });

  return (
    <div className="min-h-dvh bg-tile pb-28">
      {first ? (
        <div className="px-4 pt-[max(env(safe-area-inset-top),24px)]">
          <h1 className="text-headline font-bold text-ink">{t('profile.title')}</h1>
          <p className="mt-1 text-sm text-ink-2">{t('profile.subtitle')}</p>
        </div>
      ) : (
        <PageHeader
          title={t('profile.edit')}
          onBack={() => navigate(-1)}
          backLabel={t('common.back')}
        />
      )}
      <div className="flex flex-col gap-5 px-4 pt-5">
        {p.loading || !p.form ? (
          p.failed ? (
            <Notice
              tone="negative"
              supportRef
              title={t('common.wrong')}
              action={
                <Button size="sm" tone="outline" onClick={p.retry}>
                  {t('common.retry')}
                </Button>
              }
            />
          ) : (
            <ListSkeleton rows={3} />
          )
        ) : (
          <>
            {p.draftRestored && (
              <DraftBar
                text={t('common.draftRestored')}
                keepLabel={t('common.keep')}
                discardLabel={t('common.discard')}
                onKeep={p.keepDraft}
                onDiscard={p.discardDraft}
              />
            )}
            <Card className="space-y-4">
              <Field
                label={t('profile.name')}
                placeholder={t('profile.namePlaceholder')}
                autoComplete="name"
                value={p.form.name}
                onChange={(e) => p.patch({ name: e.target.value })}
              />
              <div>
                <p className="mb-2 text-xs font-medium text-ink-2">{t('profile.diet')}</p>
                <div role="radiogroup" aria-label={t('profile.diet')} className="flex gap-2">
                  <Pill active={p.form.diet === 'veg'} onClick={() => p.patch({ diet: 'veg' })}>
                    {t('profile.veg')}
                  </Pill>
                  <Pill
                    active={p.form.diet === 'non_veg'}
                    onClick={() => p.patch({ diet: 'non_veg' })}
                  >
                    {t('profile.nonVeg')}
                  </Pill>
                </div>
              </div>
              <Field
                label={t('profile.email')}
                placeholder={t('profile.emailPlaceholder')}
                type="email"
                autoComplete="email"
                value={p.form.email}
                onChange={(e) => p.patch({ email: e.target.value })}
              />
              <Field
                label={t('profile.birthday')}
                hint={t('profile.birthdayHint')}
                type="date"
                value={p.form.birthday}
                onChange={(e) => p.patch({ birthday: e.target.value })}
              />
              <div>
                <p className="mb-2 text-xs font-medium text-ink-2">{t('profile.language')}</p>
                <div
                  role="radiogroup"
                  aria-label={t('profile.language')}
                  className="flex flex-wrap gap-2"
                >
                  {CUSTOMER_LOCALES.map((locale) => (
                    <Pill
                      key={locale}
                      active={p.form?.locale === locale}
                      onClick={() => p.patch({ locale })}
                    >
                      {LOCALE_NAMES[locale]}
                    </Pill>
                  ))}
                </div>
              </div>
            </Card>
            {p.error && <Notice tone="negative" title={p.error} />}
          </>
        )}
      </div>
      {p.form && (
        <div className="fixed inset-x-0 bottom-0 z-30 mx-auto w-full max-w-120 bg-surface px-4 pt-3 pb-[max(env(safe-area-inset-bottom),12px)] shadow-[inset_0_1px_0_var(--color-hair)]">
          <Button full busy={p.saving} onClick={p.submit}>
            {first ? t('profile.save') : t('common.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
